import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { v2PricingPlans } from './pricingData';

// Pricing card grid shared by the homepage section and /pricing.
// Wrapper variants:
//   - featured  → orange gradient halo + "Most popular" badge
//   - Enterprise → multi-stop pastel gradient halo + "Custom built" badge
//   - default   → no halo, plain card
export default function V2PricingCards() {
  const { theme } = useTheme();
  const { token } = useAuth();
  const isDark = theme === 'dark';

  return (
    <div className="mx-auto grid max-w-[1240px] grid-cols-1 items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:items-stretch">
      {v2PricingPlans.map((plan) => {
        // Logged-in visitors skip registration and land on the dashboard with
        // checkout for the selected tier auto-started (?buy= handled there).
        const ctaHref = token && plan.buyKey ? `/dashboard/legacy?buy=${plan.buyKey}` : plan.href;
        const innerCard = (
          <div className="v2-pricing-card relative flex h-full flex-col gap-5 rounded-2xl border border-solid border-white/10 bg-[#141414] p-7">
            <div className="flex flex-col gap-1.5">
              <h3 className="text-[24px] font-semibold leading-tight text-white">{plan.name}</h3>
              <p className="text-[14px] leading-[1.4] text-white/65">{plan.description}</p>
            </div>
            <div className="flex items-end gap-1">
              <span className="text-[40px] font-semibold leading-none text-white">{plan.price}</span>
              {plan.period ? <span className="pb-1 text-[16px] text-white/65">{plan.period}</span> : null}
            </div>
            <p className="text-[12px] leading-[1.5] text-white/55">{plan.note}</p>
            <Link
              to={ctaHref}
              className={`inline-flex h-11 items-center justify-center rounded-lg px-5 text-[15px] font-medium transition-transform duration-200 hover:-translate-y-0.5 ${
                plan.featured ? 'v2-brand-cta border border-solid border-white/15' : ''
              }`}
              style={
                plan.featured
                  ? {
                      backgroundImage: 'linear-gradient(180deg, #EE9B33 1.39%, #E47708 101.39%)',
                      boxShadow:
                        'rgba(255,255,255,0.08) 0 0.5px 0.5px inset, rgba(91,53,17,0.2) 0 1px 1px, rgba(91,53,17,0.4) 0 2px 5px -2px, #FF8903 0 0 0 1px',
                      color: '#FFFFFF',
                    }
                  : {
                      backgroundColor: isDark ? '#FFFFFF' : '#0E0E0E',
                      color: isDark ? '#0E0E0E' : '#FFFFFF',
                    }
              }
            >
              {plan.cta}
            </Link>
            <div className="flex flex-col gap-3 border-t border-t-solid border-t-white/10 pt-5">
              <p className="text-[13px] font-medium text-white/55">What's included:</p>
              {plan.features.map((feature) => {
                const isHeader = feature.endsWith(':');
                return (
                  <div key={feature} className="flex items-start gap-2">
                    {!isHeader && (
                      <svg width="18" height="18" viewBox="0 0 21 20" fill="none" aria-hidden="true" className="mt-0.5 shrink-0 text-orange">
                        <path d="M5.55 10.417 8.466 13.334 14.716 7.084" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    <span className={`text-[14px] leading-[1.4] ${isHeader ? 'font-semibold text-white/85' : 'text-white/85'}`}>
                      {feature}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );

        if (plan.featured) {
          return (
            <div
              key={plan.name}
              className="relative w-full rounded-[20px] p-2 pt-10"
              style={{ backgroundImage: 'linear-gradient(180deg, #D9620C 0%, #9A4307 100%)' }}
            >
              <span className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 text-[13px] font-medium" style={{ color: '#FFFFFF' }}>
                Most popular
              </span>
              {innerCard}
            </div>
          );
        }

        if (plan.name === 'Enterprise') {
          return (
            <div
              key={plan.name}
              className="v2-enterprise-wrapper relative w-full rounded-[20px] p-2 pt-10"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), radial-gradient(75% 55% at 45% 50%, rgba(252,231,232,0.95) 0%, rgba(252,231,232,0) 60%), radial-gradient(60% 60% at 78% 30%, #F990D7 0%, transparent 55%), radial-gradient(55% 60% at 18% 35%, #A99FFF 0%, transparent 60%), radial-gradient(50% 55% at 92% 78%, #D8B8FF 0%, transparent 55%), linear-gradient(135deg, #9FB3FF 0%, #C9A4FF 40%, #FFB9E0 75%, #DDB6FF 100%)',
              }}
            >
              <span className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 text-[13px] font-medium" style={{ color: '#FFFFFF' }}>
                Custom built
              </span>
              {innerCard}
            </div>
          );
        }

        return (
          <div key={plan.name} className="relative w-full rounded-[20px] p-2 pt-10">
            {innerCard}
          </div>
        );
      })}
    </div>
  );
}
